import React, { ChangeEvent } from 'react';
import { DeliveryZoneCode } from '@noths/polaris-client-localisation';
import {
  DropDown,
  DropDownOption,
  IconInfoCircular,
} from '@noths/polaris-client-ribbons-design-system';
import { priceFormatter } from '@noths/polaris-client-utils';
import { Merge } from '@noths/polaris-dev-ts-recipes';

import { DELIVERY_AREA_ID_PREFIX } from 'src/components/organisms/DeliveryArea/components';
import { SelectCountryPayload } from 'src/redux/deliveryZoneOptions';
import { DeliveryOption, DeliveryPriceAndDateOption, Money } from 'src/types';
import { getFormattedDateString } from 'src/utils';
import {
  COUNTRY_SELECT_LABEL,
  DELIVERY_NOT_AVAILABLE_TO_COUNTRY_FIELD_ERROR,
  DELIVERY_NOT_AVAILABLE_TO_COUNTRY_MESSAGE,
  FREE_DELIVERY,
  SELECTED_COUNTRIES_DELIVERY_ONLY,
  UK_DELIVERY_ONLY,
} from '../constants';
import * as styles from '../styles';
import { DeliveryOptionSkeleton } from './DeliveryOptionSkeleton';

export type DeliveryOptionInfo = Merge<
  Pick<DeliveryPriceAndDateOption, 'date' | 'exactDate' | 'max' | 'min' | 'optionCode'>,
  Pick<DeliveryOption['service'], 'name'>
> & {
  price: Money;
};

export interface DeliveryBlockProps {
  className?: string;
  countries: DropDownOption[];
  deliveryOptions: DeliveryOptionInfo[];
  deliveryOptionsPlaceholderItems: DeliveryOption[];
  hasSelectedCountriesDeliveryOnly: boolean;
  hasUkDeliveryOnly: boolean;
  idPrefix?: string;
  selectedCountry: string;
  isLoading: boolean;
  onCountryChange: (payload: SelectCountryPayload) => void;
}

export const DeliveryBlock = ({
  className,
  countries,
  deliveryOptions,
  deliveryOptionsPlaceholderItems,
  hasSelectedCountriesDeliveryOnly,
  hasUkDeliveryOnly,
  idPrefix = '',
  selectedCountry,
  onCountryChange,
  isLoading,
}: DeliveryBlockProps) => {
  const handleCountryChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const [countryCode, deliveryZone] = e.target.value.split('|') as [string, DeliveryZoneCode];
    const sectionParent: SelectCountryPayload['sectionParent'] = idPrefix.includes(
      DELIVERY_AREA_ID_PREFIX,
    )
      ? 'delivery-area'
      : 'delivery-summary';

    onCountryChange({ selectedCountry: countryCode, selectedZone: deliveryZone, sectionParent });
  };

  const isDeliveryAvailable = Boolean(deliveryOptionsPlaceholderItems.length);

  return (
    <div css={styles.deliveryBlock} className={className}>
      <DropDown
        css={styles.countrySelect}
        options={countries}
        label={COUNTRY_SELECT_LABEL}
        id={`${idPrefix}delivery-block-countries`}
        onChange={handleCountryChange}
        value={selectedCountry}
        disabled={isLoading}
        error={isDeliveryAvailable ? null : DELIVERY_NOT_AVAILABLE_TO_COUNTRY_FIELD_ERROR}
      />
      {isDeliveryAvailable ? (
        <>
          {hasUkDeliveryOnly && (
            <div css={styles.countryInfoWrapper}>
              <IconInfoCircular width={16} height={16} />
              <p css={styles.countryInfoMessage}>{UK_DELIVERY_ONLY}</p>
            </div>
          )}
          {hasSelectedCountriesDeliveryOnly && (
            <div css={styles.countryInfoWrapper}>
              <IconInfoCircular width={16} height={16} />
              <p css={styles.countryInfoMessage}>{SELECTED_COUNTRIES_DELIVERY_ONLY}</p>
            </div>
          )}
          {isLoading ? (
            <div css={styles.deliveryOptionsList}>
              {deliveryOptionsPlaceholderItems.map(({ service: { code } }, i) => (
                <DeliveryOptionSkeleton isFirstOption={i === 0} key={`skeleton-${code}`} />
              ))}
            </div>
          ) : (
            <ul css={[styles.deliveryOptionsList, styles.fadeIn]}>
              {deliveryOptions.map(({ date, exactDate, optionCode, min, max, name, price }, i) => (
                <li css={styles.deliveryOption} key={optionCode}>
                  <p css={styles.deliveryType}>{name}</p>
                  <div css={styles.dateAndPriceWrapper}>
                    <p css={[styles.dateAndPrice, i === 0 && styles.firstDateAndPrice]}>
                      {exactDate && date
                        ? getFormattedDateString(date)
                        : `${min} - ${max} days from dispatch`}
                    </p>
                    <p css={[styles.dateAndPrice, i === 0 && styles.firstDateAndPrice]}>
                      {Number(price.amount) === 0
                        ? FREE_DELIVERY
                        : priceFormatter(price.currencyCode, Number(price.amount), false)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <p css={styles.deliveryNotAvailableMessage}>{DELIVERY_NOT_AVAILABLE_TO_COUNTRY_MESSAGE}</p>
      )}
    </div>
  );
};
